import apiClient from "@/lib/api/client";

// ── Types ──────────────────────────────────────────────────────────────────

export interface RuKpi {
  id: string;
  scope: string;
  key: string;
  label: string;
  value: string;
  raw_value: number | null;
  unit: string;
  delta: string;
  delta_dir: string;
  sort_order: number;
  note: string | null;
}

export interface RuCorpusMeta {
  id: string;
  run_id: string;
  predictions_path: string;
  truth_path: string;
  acceptance_path: string;
  n_encounters: number;
  n_predictions: number;
  dos_min_iso: string | null;
  dos_max_iso: string | null;
  practice_realization: number;
  seeded_at: string | null;
}

export interface RuOverview {
  corpus: RuCorpusMeta | null;
  kpis: { practice: RuKpi[]; in_scope: RuKpi[] };
}

export interface RuProvider {
  provider: string;
  display_name: string;
  is_in_scope: boolean;
  // Phase 0: `grade`, `grade_rank`, `peer_median_leakage`, `peer_median_delta_pp`
  // and `peer_label` were removed from this payload. They were comparative
  // performance judgements built on a ratio whose denominator counts only
  // adjudicated dollars. The backend no longer emits them for provider reads;
  // do not re-add them here without revisiting that decision.
  n_encounters: number;
  n_flagged: number;
  flagged_rate: number;
  truth_allowed: number;
  upbill_allowed: number;
  expected_cash_uplift: number;
  leakage_rate: number;
  allowed_per_encounter: number;
  upbillable_per_encounter: number;
  top_miss_label: string | null;
  top_miss_count: number;
}

export interface RuCase {
  encounter_id: string;
  claim_id: string | null;
  patient_id: string | null;
  patient_label: string | null;
  provider: string;
  provider_display: string;
  is_in_scope: boolean;
  payer: string;
  payer_bucket: string;
  dos: string;
  dos_iso: string | null;
  visit_type: string | null;
  visit_context: Record<string, unknown> | null;
  truth_allowed: number;
  truth_billed: number;
  upbill_allowed: number;
  upbill_collected: number;
  /**
   * Internal-only recall-gap totals (NEGATIVE dollars). Sum of the
   * system_miss line dollars on this encounter — i.e. truth-billed
   * codes our pipeline failed to surface. NOT shown in the default
   * upbill view; only the /revenue/internal/critical-misses page
   * displays these. Defaults to 0 when there are no recall gaps.
   */
  internal_miss_allowed?: number;
  internal_miss_collected?: number;
  leakage_rate: number;
  expected_cash_uplift: number;
  expected_cash_source: string;
  n_missed_proc: number;
  n_em_lift: number;
  n_dx_misses: number;
  n_modifier: number;
  top_miss_label: string;
  /**
   * True when the software coded chart-supported CPTs but there is no
   * submitted claim yet (freshly-signed, unbilled). An honest
   * "coded, awaiting claim" state — NOT a coding failure and NOT a $0
   * floor-out artifact. Render as a distinct, reassuring pill so a
   * correctly-coded-but-unbilled encounter never reads as "didn't code".
   */
  coded_awaiting_claim?: boolean;
  narrative: string | null;
  tier: string;
  has_critical_miss: boolean;
  has_split_sibling?: boolean;
  sibling_claim_ids?: string[];
  /**
   * Ghost-claim placeholder (truth = 99499/99199, $0 allowed) where
   * no sibling was found in this export. Zero-dollar advisory; the
   * biller must verify in Athena before adding any code. Per Dr.
   * InScope 2026-05-25: "Ghost claims are never correct. Theres an
   * explanation always."
   */
  has_ghost_unresolved?: boolean;
  /** True when this case carries any ghost-claim / split-sibling
   *  advisory (annotated by the /cases overlay). */
  is_ghost_sibling?: boolean;
  /** Distinct ghost/sibling advisory kinds present on the case. */
  ghost_kinds?: string[];
  /** True once the biller has bulk-acknowledged this ghost/sibling case. */
  ghost_resolved?: boolean;
  ghost_resolution?: {
    resolved_at: string | null;
    resolved_by: string | null;
    reason: string | null;
  } | null;
  attribution: string;
  attribution_note: string | null;
}

export interface RuLine {
  id: string;
  encounter_id: string;
  cpt: string;
  cpt_description: string | null;
  modifiers: string[] | null;
  units: number;
  dx_pointers: number[] | null;
  kind: string;
  delta: string;
  category: string;
  tier: string;
  allowed_amount: number;
  expected_allowed: number;
  expected_collected: number;
  source: string | null;
  evidence_section: string | null;
  evidence_span: string | null;
  rule_path: string | null;
  accept_n_submitted: number | null;
  accept_n_accepted: number | null;
  accept_rate: number | null;
  accept_bucket: string | null;
  /**
   * AMA / CMS citation for this proposed line (short reason + authority
   * + reference URL). Attached by the /api/revenue/cases/{id} endpoint
   * using the same resolver as /bakeoff. Lets the review surface show
   * "why is this code defensible?" inline. Optional — null when no
   * citation maps to the rule_id / CPT.
   */
  citation?: RuBakeoffCitation | null;
  /**
   * INTERNAL-ONLY line. Set on system_miss / pipeline-recall rows
   * ("truth billed it but our pipeline missed surfacing it"). These
   * carry NEGATIVE expected_allowed dollars because they represent
   * recall gaps, not biller-actionable upbill opportunities. The
   * /api/revenue/cases/{id} endpoint strips them by default; they
   * only come through when the caller passes ``internal_view=true``
   * (i.e. the internal critical-misses page).
   */
  internal_only?: boolean;
}

export interface RuCaseHtmlRefs {
  has_encounter_summary: boolean;
  has_billing_summary: boolean;
  encounter_summary_url: string | null;
  billing_summary_url: string | null;
}

/**
 * One row of the billed-summary block. Returned for every CPT that
 * was actually submitted on the original claim (truth side), so the
 * review page can show "what's billed vs. what's missing" alongside
 * the proposal diff. ``bucket`` is a coarse rule-engine-coded label
 * driven by the backend (E&M / preventive / ACP / quality measure /
 * vaccine / etc.) that powers the inline category chip.
 */
export interface RuBilledRow {
  cpt: string;
  bucket: string;
  bucket_label: string;
  description: string | null;
  modifiers: string[];
  units: number;
  dx_pointers: number[];
  allowed_amount: number;
  paid_amount: number;
  charge_amount: number;
  denial_codes: string[];
  /**
   * Canonical multi-billable flag from the backend
   * (``app.services.multi_billable``). True for a CPT that may legitimately
   * repeat on one claim (e.g. 96127). Optional/additive: absent on older
   * payloads (treat as false). Supersedes the client-side ``lib/multiBillable``
   * copy.
   */
  multi_billable?: boolean;
}

export interface RuBilledSummary {
  rows: RuBilledRow[];
  total_allowed: number;
  total_paid: number;
  total_charge: number;
  n_lines: number;          // dollar lines (allowed > 0)
  n_quality_lines: number;  // $0 Cat-II quality measures
  n_total_rows: number;
}

export interface RuCaseDetail {
  case: RuCase;
  lines: RuLine[];
  billed_summary?: RuBilledSummary;
  html_refs?: RuCaseHtmlRefs;
}

export interface RuCaseList {
  total: number;
  /**
   * Sum of ``upbill_allowed`` across the FULL filtered set (not just
   * the current page). Drives the "Upbillable" summary card so its
   * headline is $X across all 3,761 cases that match the filters,
   * not just the 50 rows currently on screen.
   */
  total_upbill_allowed?: number;
  /**
   * Sum of ``expected_cash_uplift`` across the FULL filtered set —
   * the recoverable total across every matching encounter, not just
   * the current page.
   */
  total_expected_cash_uplift?: number;
  /** Count of outstanding (unresolved) ghost/sibling advisory cases in
   *  the full filtered set. Drives the "Resolve N sibling/ghost issues"
   *  bulk-action affordance above the queue. */
  n_ghost_outstanding?: number;
  limit: number;
  offset: number;
  cases: RuCase[];
}

export interface RuPayerBucket {
  payer_bucket: string;
  n: number;
  upbill_allowed: number;
  expected_cash_uplift: number;
}

export interface RuCptRollup {
  cpt: string;
  category: string;
  n: number;
  upbill_allowed: number;
  expected_cash_uplift: number;
}

// ── Filters ────────────────────────────────────────────────────────────────

export interface CaseFilters {
  provider?: string;
  payer_bucket?: string;
  miss_category?: "em_lift" | "missed_proc" | "dx_miss" | "modifier" | "system_miss";
  has_critical_miss?: boolean;
  dos_from?: string;
  dos_to?: string;
  search?: string;
  sort_by?: string;
  direction?: "asc" | "desc";
  include_resolved?: boolean;
  limit?: number;
  offset?: number;
}

// ── API functions ──────────────────────────────────────────────────────────

export async function fetchRevenueOverview(): Promise<RuOverview> {
  const { data } = await apiClient.get("/revenue/overview");
  return data;
}

export async function fetchRevenueProviders(opts: {
  sort_by?: string; direction?: "asc" | "desc";
} = {}): Promise<{ providers: RuProvider[]; n: number }> {
  const { data } = await apiClient.get("/revenue/providers", { params: opts });
  return data;
}

export async function fetchRevenueProvider(provider: string): Promise<{ provider: RuProvider; top_cases: RuCase[] }> {
  const { data } = await apiClient.get(`/revenue/providers/${encodeURIComponent(provider)}`);
  return data;
}

export async function fetchRevenueCases(filters: CaseFilters = {}): Promise<RuCaseList> {
  const { data } = await apiClient.get("/revenue/cases", { params: filters });
  return data;
}

export async function fetchRevenueCase(
  encounterId: string,
  opts: { internalView?: boolean } = {},
): Promise<RuCaseDetail> {
  const params: Record<string, string> = {};
  if (opts.internalView) {
    // Pulls system_miss / pipeline-recall rows back into the response.
    // Default off — see RuLine.internal_only doc-comment.
    params.internal_view = "true";
  }
  const { data } = await apiClient.get(
    `/revenue/cases/${encodeURIComponent(encounterId)}`,
    { params },
  );
  return data;
}

// ── Biller RPA write-out payload ─────────────────────────────────────────
// Turns a finished case into a ready-to-push Athena billing payload so the
// biller never edits JSON. Drives the biller-friendly write-out form: pick
// a case -> review these lines -> pick a test patient -> write to Athena.

export interface RuBillingService {
  code: string;
  modifiers: string[];
  units: string;
  icdCodes: string[];
}

export interface RuBillingEm extends RuBillingService {
  g2211?: { modifiers: string[]; units: string; icdCodes: string[] } | null;
}

export interface RuBillingData {
  emCode: RuBillingEm | null;
  labCodes: RuBillingService[];
  questionnaireCodes: RuBillingService[];
  additionalServices: RuBillingService[];
  cptIICodes: RuBillingService[];
}

export type RuLineRecommendationState =
  | "recommended"
  | "candidate"
  | "suppressed";
export type RuLineReviewState =
  | "proposed"
  | "edited"
  | "added"
  | "removed"
  | "unchanged";
export type RuLineWriteState =
  | "not_written"
  | "written"
  | "already_in_athena"
  | "failed";
export type RuLineValidationState = "clean" | "warning" | "error";

/**
 * The four orthogonal per-line state dimensions the cockpit reads off each
 * served preview row (BE-02A), keyed by ``stable_line_key``. Computed
 * server-side from real signals (line tier/status/origin/source, the durable
 * submitted-charge store, and the Verify claim-correctness checks). Each is
 * additive/optional — absent on legacy payloads; treat the documented default
 * as the fallback.
 */
export interface RuLineStateMeta {
  /** Engine posture: 'recommended' | 'candidate' | 'suppressed' (default 'recommended'). */
  recommendation_state?: RuLineRecommendationState;
  /** Human-review posture: proposed|edited|added|removed|unchanged (default 'unchanged'). */
  review_state?: RuLineReviewState;
  /** Athena write posture: not_written|written|already_in_athena|failed (default 'not_written'). */
  write_state?: RuLineWriteState;
  /** Claim-correctness posture: clean|warning|error (default 'clean'). */
  validation_state?: RuLineValidationState;
}

export interface RuBillingPreviewRow extends RuLineStateMeta {
  cpt: string;
  description: string | null;
  modifiers: string[];
  units: string;
  icdCodes?: string[];
  bucket: "em" | "lab" | "questionnaire" | "additional" | "cptII";
  amount: number;
  /**
   * BE-04: richer amount metadata alongside the KEPT ``amount`` float
   * (backward compat). ``amount_cents`` is the amount as an integer count of
   * cents (no float drift); ``amount_kind`` names what the number represents so
   * the FE labels it correctly; ``label``/``currency`` are display metadata.
   * Additive/optional: absent on legacy payloads (fall back to ``amount``).
   */
  amount_cents?: number;
  amount_kind?:
    | "expected_allowed"
    | "expected_collected"
    | "billed_charge"
    | "estimate"
    | "cat_ii";
  label?: string;
  currency?: string;
  origin: "proposed" | "billed";
  /**
   * Canonical multi-billable flag from the backend
   * (``app.services.multi_billable``). True for a CPT that may legitimately
   * repeat on one claim as separate line items (e.g. 96127), so the biller UI
   * appends rather than dedupes. Optional/additive: absent on older payloads
   * (treat as false). Supersedes the client-side ``lib/multiBillable`` copy.
   */
  multi_billable?: boolean;

  /**
   * Durable per-line identity (PK of ``serving.lines``, minted in the serving
   * layer). Projected onto served rows so the FE can attach evidence,
   * validation, and reconciliation to the EXACT physical line — duplicate CPTs
   * carry DISTINCT keys. Additive/optional (BE-01): absent on legacy payloads.
   * FE reconciliation rewiring to consume this is a later wave (FE-12); for now
   * the field just flows through.
   */
  stable_line_key?: string;
}

export interface RuBillingAdvisoryRow {
  cpt: string;
  description: string | null;
  reason: string;
}

export interface RuBillingPayload {
  billing_data: RuBillingData;
  line_preview: RuBillingPreviewRow[];
  advisory: RuBillingAdvisoryRow[];
  case_summary: {
    encounter_id: string;
    claim_id: string | null;
    patient_label: string | null;
    patient_id: string | null;
    provider_display: string | null;
    payer: string | null;
    dos: string | null;
    expected_cash_uplift: number | null;
  };
  totals: { n_billable: number; expected_uplift: number };
}

export async function fetchCaseBillingPayload(
  encounterId: string,
): Promise<RuBillingPayload> {
  const { data } = await apiClient.get(
    `/revenue/cases/${encodeURIComponent(encounterId)}/billing-payload`,
  );
  return data;
}

// Claim-correctness validation for the (possibly edited) write-out line set.
// Advisory only — the biller can still write out with an override.

export type RuLineWarningSeverity = "error" | "warning" | "info";

export interface RuLineWarning {
  severity: RuLineWarningSeverity;
  message: string;
  /**
   * Which field on the line the warning addresses (BE-03). "cpt" for
   * procedure-code warnings, "icd" for dx-pointer warnings. Additive/optional:
   * absent on the by-code ``warnings`` map and on legacy payloads.
   */
  field?: "cpt" | "icd";
  /** The offending ICD-10 code when ``field === 'icd'`` (additive, BE-03). */
  code?: string;
}

export interface RuValidateResult {
  warnings: Record<string, RuLineWarning[]>;
  /**
   * Warnings keyed by the durable ``stable_line_key`` (BE-03), so two
   * duplicate-CPT lines get INDEPENDENTLY-addressed warnings (the by-code
   * ``warnings`` map above collapses them). Additive/optional: absent on legacy
   * payloads — fall back to ``warnings`` (by code) then. FE rewiring to prefer
   * this is a later wave (FE-12).
   */
  warnings_by_line_id?: Record<string, RuLineWarning[]>;
  summary: {
    n_flagged_cpts: number;
    n_error: number;
    n_warning: number;
    ok: boolean;
  };
}

export async function validateCaseLines(
  encounterId: string,
  lines: { cpt: string }[],
): Promise<RuValidateResult> {
  const { data } = await apiClient.post(
    `/revenue/cases/${encodeURIComponent(encounterId)}/validate-lines`,
    { lines },
  );
  return data;
}

// ── Ghost / split-sibling resolution ─────────────────────────────────────
// "If it's on a sibling, it's not real." Ghost-claim / split-sibling
// advisories are zero-dollar "verify in Athena" notes; the biller can
// bulk-resolve them all so they clear out of the active queue. Resolution
// is persisted server-side and survives snapshot/pipeline rebuilds.

export interface RuGhostSummary {
  n_total: number;
  n_resolved: number;
  n_outstanding: number;
  n_pure_ghost: number;
  store_count: number;
}

export async function fetchGhostSummary(): Promise<RuGhostSummary> {
  const { data } = await apiClient.get("/revenue/cases/ghost/summary");
  return data;
}

export async function fetchGhostCases(opts: {
  resolved?: boolean;
  limit?: number;
} = {}): Promise<{ n: number; cases: RuCase[] }> {
  const { data } = await apiClient.get("/revenue/cases/ghost/list", {
    params: opts,
  });
  return data;
}

export async function resolveAllGhostCases(opts: {
  resolved_by?: string;
  reason?: string;
} = {}): Promise<{ resolved_now: number; summary: RuGhostSummary }> {
  const { data } = await apiClient.post(
    "/revenue/cases/ghost/resolve-all",
    opts,
  );
  return data;
}

export async function resolveGhostCases(
  encounterIds: string[],
  opts: { resolved_by?: string; reason?: string } = {},
): Promise<{ resolved_now: number; summary: RuGhostSummary }> {
  const { data } = await apiClient.post("/revenue/cases/ghost/resolve", {
    encounter_ids: encounterIds,
    ...opts,
  });
  return data;
}

export async function unresolveGhostCases(
  opts: { encounter_ids?: string[]; all?: boolean } = {},
): Promise<{ unresolved_now: number; summary: RuGhostSummary }> {
  const { data } = await apiClient.post(
    "/revenue/cases/ghost/unresolve",
    opts,
  );
  return data;
}

export async function fetchCriticalMisses(opts: {
  limit?: number; offset?: number;
} = {}): Promise<{ warning: string; total: number; encounters: RuCase[] }> {
  const { data } = await apiClient.get("/revenue/_internal/critical-misses", { params: opts });
  return data;
}

export async function fetchRevenueByPayer(): Promise<RuPayerBucket[]> {
  const { data } = await apiClient.get("/revenue/aggregates/by-payer");
  return data.buckets;
}

export async function fetchRevenueByCpt(opts: { limit?: number } = {}): Promise<RuCptRollup[]> {
  const { data } = await apiClient.get("/revenue/aggregates/by-cpt", { params: opts });
  return data.cpts;
}

// ── Insights: confusion-matrix billing accuracy (Sensai vs Coder+) ───────────
// Set-based, code-level TP/FP/FN + precision/recall/F1 on coded-only
// encounters, comparing Sensai committed (tier==primary) predicted CPTs to
// Coder+ truth-billed CPTs for the ACTIVE practice (scoped server-side by the
// authenticated user's practice — never a client param). Both cuts are
// returned in one response so the "exclude non-billable Cat-II" radio toggle
// flips client-side with no refetch.

export interface RuAccuracyCut {
  n_coded_encounters: number;
  tp: number;
  fp: number;
  fn: number;
  precision: number;
  recall: number;
  f1: number;
  perfect_match_encounters: number;
}

export interface RuAccuracy {
  practice: string;
  tenant: string;
  ingested: boolean;
  exclude_nonbillable: boolean;
  n_truth_coded: number;
  coded_encounters_with_pred_row: number;
  dropped_other_tenant_pred_rows: number;
  /** Cat-II quality codes (\d{4}F) INCLUDED on both sides. */
  all_codes: RuAccuracyCut;
  /** Cat-II quality codes EXCLUDED from both sides before matching. */
  billable_only: RuAccuracyCut;
  /** Mirror of whichever cut the `exclude_nonbillable` query param selected. */
  active: RuAccuracyCut;
  built_at: number | null;
}

export async function fetchRevenueAccuracy(opts: {
  excludeNonbillable?: boolean;
} = {}): Promise<RuAccuracy> {
  const { data } = await apiClient.get("/revenue/insights/accuracy", {
    params: { exclude_nonbillable: opts.excludeNonbillable ?? true },
  });
  return data;
}

// ── Pending Evidence (chart-data scrape worklist) ────────────────────────
// Surfaces the queue of outstanding artifact-resolution requests that the
// RPA runner has not yet processed. Pure planning readout; does not
// affect billing predictions until resolutions actually land.

export interface RuPendingFamily {
  family: string;
  n_requests: number;
  n_unique_claims: number;
  top_cpts: { cpt: string; n: number }[];
}

export interface RuPendingBucket {
  key: string;
  n: number;
}

export interface RuPendingFile {
  name: string;
  rows: number;
  mtime: number;
}

export interface RuPendingEvidence {
  total_requests: number;
  unique_claims: number;
  excluded_note_rescrape: number;
  by_family: RuPendingFamily[];
  by_department: RuPendingBucket[];
  by_payer: RuPendingBucket[];
  by_cpt: RuPendingBucket[];
  files: RuPendingFile[];
  built_at: number;
  source_mtime: number;
  note: string;
}

export async function fetchPendingEvidence(): Promise<RuPendingEvidence> {
  const { data } = await apiClient.get("/revenue/pending-evidence");
  return data;
}


// ── Cases leaderboard ──────────────────────────────────────────────────
// Aggregates the same filtered upbill-eligible case list rolled up by
// (supportable CPT × flag kind). Drives the Leaderboard tab on the
// /revenue/cases page — same filters as the queue, different cut.
// Per the focus provider 2026-05-25.

/** One contributing-encounter row inside a leaderboard bucket. Each is
 *  a clickable deep-link into the full case detail in /revenue/cases. */
export interface RuLeaderboardExample {
  encounter_id: string;
  dos_iso: string | null;
  provider: string;
  provider_display: string | null;
  payer_bucket: string | null;
  claim_id: string | null;
  is_in_scope: boolean;
  upbill_allowed: number;
  expected_cash: number;
  narrative: string | null;
}

/** Aggregated bucket: every encounter in the filtered set whose `_items`
 *  include at least one upbillable line for this `(cpt, kind)` cell.
 *  `examples` carries the top-N highest-impact contributing encounters. */
export interface RuLeaderboardRow {
  cpt: string;
  kind: string;          // raw kind ("underleveled_em", "missed_procedure", ...)
  kind_label: string;    // display label ("E&M Lift", "Missed Procedure", ...)
  category: string;      // ("em_lift", "missed_proc", ...)
  n_encounters: number;
  upbill_allowed: number;
  expected_cash: number;
  median_per_encounter: number;
  top_payer: string | null;
  examples: RuLeaderboardExample[];
}

export interface RuLeaderboardCategory {
  category: string;
  n_cpts: number;
  n_encounters: number;   // SUM not unique (one encounter -> many buckets)
  upbill_allowed: number;
  expected_cash: number;
  cpts: string[];
}

export interface RuCasesLeaderboard {
  total_n_encounters: number;
  total_upbill_allowed: number;
  total_expected_cash_uplift: number;
  n_rows: number;
  rows: RuLeaderboardRow[];
  by_category: RuLeaderboardCategory[];
}

/** Filter passthrough — every parameter is the same shape /cases takes. */
export type LeaderboardFilters = Omit<CaseFilters, "sort_by" | "direction" | "limit" | "offset"> & {
  /** Cap on returned rows (default 200). Server-side. */
  limit?: number;
  /** Examples surfaced per row for drill-down (default 10, per Dr.
   *  InScope 2026-05-25 "pick up to first 10"). */
  examples_per_row?: number;
};

export async function fetchRevenueCasesLeaderboard(
  filters: LeaderboardFilters = {},
): Promise<RuCasesLeaderboard> {
  const { data } = await apiClient.get("/revenue/cases/leaderboard", {
    params: filters,
  });
  return data;
}

// ── Cat-II recall-gap leaderboard ───────────────────────────────────────
// Dollar-free sibling of the CPT × flag leaderboard, dedicated to
// Category-II quality-measure codes (CPT-II: four digits + "F", e.g.
// 3008F). Ranks measures by capture volume so recall gaps ("we almost
// never bill 3074F") surface. Lives on its own tab so it never pollutes
// the E&M/CPT dollar leaderboard.

/** One contributing encounter inside a Cat-II leaderboard row. */
export interface RuCatIIExample {
  encounter_id: string;
  dos_iso: string | null;
  provider: string;
  provider_display: string | null;
  payer_bucket: string | null;
  claim_id: string | null;
  is_in_scope: boolean;
  /** "missed" = truth billed it, our prediction didn't (the recall gap);
   *  "captured" = our prediction emitted it. */
  status?: "missed" | "captured";
}

/** One Category-II measure code with capture volume AND the recall gap.
 *  ``n_encounters`` = encounters where our prediction CAPTURED the code;
 *  ``n_missed`` = encounters where TRUTH billed it (chart-supported) but we
 *  missed it; ``recall`` = captured / (captured + missed). No dollar fields —
 *  Cat-II codes are informational. */
export interface RuCatIIRow {
  cpt: string;
  measure_label: string | null;
  n_encounters: number;
  n_missed: number;
  recall: number | null;
  top_payer: string | null;
  examples: RuCatIIExample[];
}

export interface RuCatIILeaderboard {
  total_n_encounters: number;
  n_encounters_with_catii: number;
  total_captures: number;
  total_missed: number;
  overall_recall: number | null;
  n_rows: number;
  rows: RuCatIIRow[];
}

/** Filter passthrough for the Cat-II leaderboard. Mirrors the dollar
 *  leaderboard minus the flag/miss dimensions (Cat-II has no flags). */
export interface CatIILeaderboardFilters {
  provider?: string;
  payer_bucket?: string;
  dos_from?: string;
  dos_to?: string;
  search?: string;
  limit?: number;
  examples_per_row?: number;
}

export async function fetchRevenueCatIILeaderboard(
  filters: CatIILeaderboardFilters = {},
): Promise<RuCatIILeaderboard> {
  const { data } = await apiClient.get("/revenue/cases/catii-leaderboard", {
    params: filters,
  });
  return data;
}

// ── Modifier gap leaderboard ────────────────────────────────────────────
// Sibling of the Cat-II leaderboard, but for CPT MODIFIERS (95, 25, 59,
// QW, GT, 93, ...). A "gap" is a modifier the human biller actually put on
// a charge (truth) that our prediction did NOT stamp on the same CPT — e.g.
// telehealth 95 on an office E&M. Ranked by gap size so the biggest
// systematic modifier misses surface as fixable stamp rules.

/** One contributing (encounter, cpt) charge inside a modifier gap row. */
export interface RuModifierGapExample {
  encounter_id: string;
  cpt: string;
  dos_iso: string | null;
  provider: string;
  provider_display: string | null;
  payer_bucket: string | null;
  claim_id: string | null;
  is_in_scope: boolean;
  /** "missed" = truth had the modifier on this CPT, our prediction didn't;
   *  "captured" = our prediction stamped it. */
  status?: "missed" | "captured";
}

/** Which CPTs drive a modifier's gap (top few, biggest first). */
export interface RuModifierGapCpt {
  cpt: string;
  n: number;
}

/** One modifier with capture-vs-gap counts.
 *  ``n_captured`` = charges where our prediction stamped it AND truth agreed;
 *  ``n_missed`` = charges where TRUTH had it on a CPT but we didn't stamp it;
 *  ``n_extra`` = charges where we stamped it but truth (which billed that CPT)
 *  did not — a potential over-stamp; ``recall`` = captured / (captured+missed). */
export interface RuModifierGapRow {
  modifier: string;
  label: string | null;
  n_captured: number;
  n_missed: number;
  n_extra: number;
  recall: number | null;
  top_payer: string | null;
  top_cpts: RuModifierGapCpt[];
  examples: RuModifierGapExample[];
}

export interface RuModifierGapLeaderboard {
  total_n_encounters: number;
  total_captured: number;
  total_missed: number;
  total_extra: number;
  overall_recall: number | null;
  n_rows: number;
  rows: RuModifierGapRow[];
}

/** Filter passthrough for the modifier gap leaderboard (same shape as
 *  the Cat-II leaderboard). */
export interface ModifierGapLeaderboardFilters {
  provider?: string;
  payer_bucket?: string;
  dos_from?: string;
  dos_to?: string;
  search?: string;
  limit?: number;
  examples_per_row?: number;
}

export async function fetchRevenueModifierGapLeaderboard(
  filters: ModifierGapLeaderboardFilters = {},
): Promise<RuModifierGapLeaderboard> {
  const { data } = await apiClient.get("/revenue/cases/modifier-gap-leaderboard", {
    params: filters,
  });
  return data;
}

// ── Recall-gap leaderboard (internal) ───────────────────────────────────
// "Where the rules engine is worse than truth," rolled up by CPT and
// rule family. Drives /revenue/internal/recall-gaps. Engineering uses
// this to prioritize which detector to write next.
export interface RuRecallGapKpis {
  total_miss_dollars: number;
  total_miss_collected: number;
  n_encounters: number;
  n_lines: number;
  n_distinct_cpts: number;
  n_distinct_families: number;
  /** How many (encounter, cpt) gaps the biller has dismissed (reject/
   *  suppress) for the active tenant. These are filtered out of the
   *  leaderboard live — see the /recall-gaps/dismiss endpoint. */
  n_dismissed_pairs?: number;
  /** Per-detection-status dollar/line breakdown. Drives the headline
   *  KPI cards: how much of the recall-gap pile is engineering work
   *  (unshipped + candidate_review) vs biller worklist (no chart
   *  evidence) vs residual after current detectors run (shipped). */
  by_detection_status?: Record<
    "shipped" | "candidate_review" | "unshipped" | "no_chart_evidence" | "unmapped",
    { miss_allowed: number; n_lines: number }
  >;
}

/** Where a CPT sits in the rules-engine pipeline.
 *
 *  - ``shipped``           - high-precision detector live; misses are bugs
 *  - ``candidate_review``  - detector emits at tier=candidate (biller reviews)
 *  - ``unshipped``         - mapped in taxonomy but no detector yet
 *  - ``no_chart_evidence`` - chart has no anchor; recovery is biller-only
 *  - ``unmapped``          - CPT not in the taxonomy
 */
export type DetectionStatus =
  | "shipped"
  | "candidate_review"
  | "unshipped"
  | "no_chart_evidence"
  | "unmapped";

// FP simulator: per-CPT confusion matrix when we project the keyword
// set over the whole corpus. Decides whether a candidate detector
// would actually be safe to ship.

/** Per-encounter example chip in the simulator output. Carries both
 *  encounter_id (chart-export key) and claim_id (the biller's working
 *  identifier). claim_id may be null on ghost / unclaimed encounters;
 *  the UI falls back to encounter_id in that case. */
export interface RuRecallSimExample {
  encounter_id: string;
  claim_id: string | null;
}

export interface RuRecallGapSimulation {
  tp: number;
  fp: number;
  fn: number;
  tn: number;
  tp_allowed: number;
  precision: number | null;
  recall: number | null;
  median_allowed: number;
  projected_recovered_dollars: number;
  projected_at_risk_dollars: number;
  family: string;
  family_label: string;
  tp_examples: RuRecallSimExample[];
  fp_examples: RuRecallSimExample[];
  fn_examples: RuRecallSimExample[];
  /** Section names the keyword sweep is scoped to for this CPT. The
   *  simulator tests its keyword set only against text inside these
   *  parsed-chart sections (HPI / A&P / orders / etc.). Stable
   *  vocabulary, see backend ``recall_gap_taxonomy.SECTION_SCOPE``. */
  section_scope: string[];
  /** Family-level scoring (TP = match AND ANY sibling truth-billed).
   *  Only populated when the CPT belongs to a cross-substitutable
   *  family (resp PCR multiplex, injection-admin variants, ear-proc
   *  pair, etc.). For these families the strict-CPT precision is
   *  artificially low because Athena's structured order/admin line
   *  is shared across siblings. The verdict heuristic prefers
   *  family-level metrics when they're materially better. */
  family_uses_substitution: boolean;
  family_siblings: string[];
  family_tp: number;
  family_fp: number;
  family_fn: number;
  family_tp_allowed: number;
  family_precision: number | null;
  family_recall: number | null;
  family_projected_recovered_dollars: number;
}

export type RecallGapVerdict =
  | "ship" | "ship_with_review" | "tune_keywords" | "skip";

export interface RuRecallGapRecommendation {
  verdict: RecallGapVerdict;
  reason: string;
  /** Which precision/recall the verdict was computed against —
   *  "strict" (this exact CPT) or "family" (any sibling counts as TP).
   *  Surfaced in the UI so reviewers can see when family-mode was
   *  used (and which sibling set). */
  scoring_mode?: "strict" | "family";
}

export interface RuRecallGapCpt {
  cpt: string;
  family: string;
  family_label: string;
  /** Detection-status badge: shipped / candidate_review / unshipped /
   *  no_chart_evidence / unmapped. Tells the leaderboard reader at a
   *  glance whether this gap is bug-shaped (shipped detector failed),
   *  scope-shaped (no detector yet), or out-of-scope (biller worklist). */
  detection_status?: DetectionStatus;
  n_lines: number;
  n_encounters: number;
  miss_allowed: number;
  miss_collected: number;
  median_per_line: number;
  first_seen: string | null;
  last_seen: string | null;
  top_payer: string | null;
  /** ``null`` when no keywords are configured for this CPT yet. */
  chart_evidence_rate: number | null;
  /** Sample size behind the rate (capped per CPT to keep the build cheap). */
  chart_evidence_n: number;
  encounter_ids: string[];
  /** Present only on the simulate endpoint. */
  simulation?: RuRecallGapSimulation | null;
  /** Present only on the simulate endpoint. */
  recommendation?: RuRecallGapRecommendation;
}

export interface RuRecallGapFamily {
  family: string;
  family_label: string;
  n_cpts: number;
  cpts: string[];
  n_lines: number;
  n_encounters: number;
  miss_allowed: number;
  miss_collected: number;
  chart_evidence_rate: number | null;
  chart_evidence_n: number;
}

export interface RuRecallGapSummary {
  kpis: RuRecallGapKpis;
  by_cpt: RuRecallGapCpt[];
  by_family: RuRecallGapFamily[];
  /** Over-fire (false-positive) companion block — same filters. */
  overfire?: RuOverfireSummary;
  filters_echo: Record<string, unknown>;
  warning: string;
  /** Demo-role pin: Columbia Medical Pediatrics, all time. Absent otherwise. */
  scope?: {
    practice: string;
    department: string;
    label: string;
    all_time: boolean;
    vaccines_included: boolean;
  };
  /** Present on /simulate endpoint only. */
  simulation_meta?: {
    corpus_n_encounters: number;
    corpus_n_with_html: number;
    built_at: number;
  };
  /** Present on /simulate endpoint only — total $ recovered per verdict bucket. */
  verdict_totals_recovered?: Record<RecallGapVerdict, number>;
}

export interface RuRecallGapEncounter {
  encounter_id: string;
  claim_id: string | null;
  dos_iso: string | null;
  provider_display: string;
  is_in_scope: boolean;
  payer_bucket: string;
  miss_allowed: number;
  miss_collected: number;
  has_chart_evidence: boolean | null;
}

export interface RuRecallGapCptDrilldown {
  cpt: string;
  n_encounters: number;
  total_miss_allowed: number;
  encounters: RuRecallGapEncounter[];
  warning: string;
}

// ── Over-fire (false-positive) analysis ──────────────────────────────────
// Mirror of the recall-gap page: for every CPT the engine EMITTED that
// truth did NOT bill, is the over-firing JUSTIFIED (chart supports it →
// the practice under-coded) or an UNJUSTIFIED false positive to tune?
export type OverfireVerdict =
  | "justified_undercoded"
  | "unjustified_fp"
  | "indeterminate";

export interface RuOverfireKpis {
  n_overfire_lines: number;
  n_encounters: number;
  n_distinct_cpts: number;
  overfire_allowed: number;
  n_justified_undercoded: number;
  n_unjustified_fp: number;
  n_indeterminate: number;
  /** Of judgeable lines, fraction the chart supports (practice under-coding). */
  justified_rate: number | null;
}

export interface RuOverfireCpt {
  cpt: string;
  family: string;
  family_label: string;
  detection_status?: DetectionStatus;
  n_overfire_lines: number;
  n_encounters: number;
  overfire_allowed: number;
  n_justified_undercoded: number;
  n_unjustified_fp: number;
  n_indeterminate: number;
  justified_rate: number | null;
  encounter_ids: string[];
}

export interface RuOverfireSummary {
  kpis: RuOverfireKpis;
  by_cpt: RuOverfireCpt[];
  filters_echo: Record<string, unknown>;
  warning: string;
}

export interface RuOverfireEncounter {
  encounter_id: string;
  claim_id: string | null;
  dos_iso: string | null;
  provider_display: string;
  is_in_scope: boolean;
  payer_bucket: string;
  overfire_allowed: number;
  justification: OverfireVerdict;
  evidence_span: string | null;
}

export interface RuOverfireCptDrilldown {
  cpt: string;
  n_encounters: number;
  n_justified_undercoded: number;
  n_unjustified_fp: number;
  n_indeterminate: number;
  total_overfire_allowed: number;
  encounters: RuOverfireEncounter[];
  warning: string;
}

export async function fetchRecallGapSummary(params: {
  provider?: string;
  payer_bucket?: string;
  dos_from?: string;
  dos_to?: string;
  cpt_search?: string;
  exclude_vaccines?: boolean;
  detection_status?: DetectionStatus[];
} = {}): Promise<RuRecallGapSummary> {
  const { data } = await apiClient.get("/revenue/_internal/recall-gaps", {
    params,
    paramsSerializer: { indexes: null },
  });
  return data;
}

/**
 * /recall-gaps/simulate — same shape as fetchRecallGapSummary but each
 * by_cpt row carries a ``simulation`` block (TP/FP/precision/recall +
 * projected dollars) and a ``recommendation`` block (ship /
 * ship_with_review / tune_keywords / skip + reason). First call after
 * a snapshot rebuild does a full corpus scan and may take ~30s; later
 * calls are cached.
 */
export async function fetchRecallGapSimulation(params: {
  provider?: string;
  payer_bucket?: string;
  dos_from?: string;
  dos_to?: string;
  cpt_search?: string;
  exclude_vaccines?: boolean;
  detection_status?: DetectionStatus[];
} = {}): Promise<RuRecallGapSummary> {
  const { data } = await apiClient.get(
    "/revenue/_internal/recall-gaps/simulate",
    { params, timeout: 120_000, paramsSerializer: { indexes: null } },
  );
  return data;
}

export async function fetchRecallGapCptDrilldown(
  cpt: string,
  params: {
    provider?: string;
    payer_bucket?: string;
    dos_from?: string;
    dos_to?: string;
    limit?: number;
  } = {},
): Promise<RuRecallGapCptDrilldown> {
  const { data } = await apiClient.get(
    `/revenue/_internal/recall-gaps/cpt/${encodeURIComponent(cpt)}`,
    { params },
  );
  return data;
}

/**
 * Over-fire (false-positive) leaderboard — the mirror of the recall-gap
 * summary. For every CPT the engine EMITTED that truth did NOT bill, rolls
 * up a justification breakdown (justified_undercoded / unjustified_fp /
 * indeterminate). Also embedded under ``overfire`` on fetchRecallGapSummary;
 * this standalone call is for the over-fire-only view.
 */
export async function fetchOverfireSummary(params: {
  provider?: string;
  payer_bucket?: string;
  dos_from?: string;
  dos_to?: string;
  cpt_search?: string;
  exclude_vaccines?: boolean;
  detection_status?: DetectionStatus[];
} = {}): Promise<RuOverfireSummary> {
  const { data } = await apiClient.get(
    "/revenue/_internal/recall-gaps/overfire",
    { params, timeout: 120_000, paramsSerializer: { indexes: null } },
  );
  return data;
}

export async function fetchOverfireCptDrilldown(
  cpt: string,
  params: {
    provider?: string;
    payer_bucket?: string;
    dos_from?: string;
    dos_to?: string;
    justification?: OverfireVerdict;
    limit?: number;
  } = {},
): Promise<RuOverfireCptDrilldown> {
  const { data } = await apiClient.get(
    `/revenue/_internal/recall-gaps/overfire/cpt/${encodeURIComponent(cpt)}`,
    { params },
  );
  return data;
}

// ── Recall-gap dismissal (loop-closer) ───────────────────────────────────
// A biller working the leaderboard can mark a row "not a real gap"
// (reject) or "never flag this for cases like this" (suppress). The
// dismissal is written to the tenant decisions log and subtracted from
// the recall-gap population on the next request — no pipeline rerun. An
// ``accept`` restores a previously-dismissed row (affirms the gap is
// real). See backend recall_gaps.dismissed_pairs.
export type RecallGapDismissAction = "reject" | "suppress" | "accept";

export interface RecallGapDismissResult {
  ok: boolean;
  /** True when the action removed the row from the leaderboard. */
  dismissed: boolean;
  /** Refreshed count of dismissed (encounter, cpt) pairs for the tenant. */
  n_dismissed_pairs: number;
  event: Record<string, unknown>;
}

export async function dismissRecallGap(params: {
  encounter_id: string;
  cpt: string;
  action?: RecallGapDismissAction;
  note?: string;
  actor?: string;
  tenant?: string;
  expected_allowed?: number;
}): Promise<RecallGapDismissResult> {
  const { data } = await apiClient.post(
    "/revenue/_internal/recall-gaps/dismiss",
    { action: "reject", ...params },
  );
  return data;
}

// ── Sibling-substitution biller report ───────────────────────────────────
export interface RuSiblingSubEncounter {
  encounter_id: string;
  claim_id: string | null;
  dos_iso: string | null;
  provider_display: string | null;
  payer_bucket: string | null;
  is_in_scope: boolean;
  truth_allowed: number;
  all_predicted_siblings: string[];
}

export interface RuSiblingSubPair {
  truth_cpt: string;
  predicted_cpt: string;
  family: string;
  family_label: string;
  n_encounters: number;
  truth_median_allowed: number;
  predicted_median_allowed: number;
  delta_per_encounter: number;
  total_delta: number;
  encounters: RuSiblingSubEncounter[];
}

export interface RuSiblingSubReport {
  n_pairs: number;
  n_encounters: number;
  total_delta: number;
  by_pair: RuSiblingSubPair[];
  filters_echo: Record<string, unknown>;
  warning: string;
}

export async function fetchSiblingSubstitutions(params: {
  provider?: string;
  payer_bucket?: string;
  dos_from?: string;
  dos_to?: string;
  exclude_vaccines?: boolean;
} = {}): Promise<RuSiblingSubReport> {
  const { data } = await apiClient.get(
    "/revenue/_internal/sibling-substitutions",
    { params },
  );
  return data;
}

// ── Policy-review catalog (no-chart-evidence CPTs) ───────────────────────
export type PolicyReviewCategory =
  | "templated_boilerplate"
  | "billing_only"
  | "sibling_substitution"
  | "non_cpt_placeholder";

export interface RuPolicyReviewRow {
  cpt: string;
  description: string;
  category: PolicyReviewCategory;
  reason: string;
  practice_action: string;
  audited_at: string;
  truth_n_lines: number;
  truth_n_encounters: number;
  truth_total_allowed: number;
}

export interface RuPolicyReviewCategorySummary {
  category: PolicyReviewCategory;
  n_cpts: number;
  total_lines: number;
  total_allowed: number;
}

export interface RuPolicyReviewCatalog {
  rows: RuPolicyReviewRow[];
  by_category: RuPolicyReviewCategorySummary[];
  kpis: {
    n_cpts: number;
    total_truth_lines: number;
    total_truth_allowed: number;
    n_encounters: number;
  };
  warning: string;
}

export async function fetchPolicyReviewCatalog(): Promise<RuPolicyReviewCatalog> {
  const { data } = await apiClient.get("/revenue/_internal/policy-review");
  return data;
}


// ── Performance: Coder+ vs Sensai counterfactual ───────────────────────────

export interface RuPerformanceKpis {
  n_encounters: number;
  n_scored_total: number;
  n_truth_comparable: number;
  n_agreed_no_diff: number;
  n_with_upbill: number;
  coverage_pct: number;
  truth_allowed: number;
  truth_expected_allowed: number;
  sensai_additional_allowed: number;
  expected_cash_uplift: number;
  lift_pct: number;
  expected_cash_pct: number;
  realization: number;
}

export interface RuPerformanceMonth {
  month: string;
  n_encounters: number;
  n_flagged: number;
  truth_allowed: number;
  truth_realized_allowed: number;
  sensai_additional_allowed: number;
  expected_cash_uplift: number;
  combined_allowed: number;
  lift_pct: number;
  expected_cash_pct: number;
}

export interface RuPerformanceProvider {
  provider: string;
  provider_display: string;
  is_in_scope: boolean;
  n_encounters: number;
  n_flagged: number;
  truth_allowed: number;
  sensai_additional_allowed: number;
  expected_cash_uplift: number;
  truth_per_encounter: number;
  additional_per_encounter: number;
  lift_pct: number;
  flagged_rate: number;
  peer_median_additional_per_encounter: number;
  peer_delta_per_encounter: number;
}

export interface RuPerformanceFunnelStep {
  id: string;
  label: string;
  n: number;
  pct_of_top: number;
}

export interface RuPerformanceHonestyBucket {
  label: string;
  is_win: boolean;
  miss_allowed: number;
  n_lines: number;
}

export interface RuPerformanceHonesty {
  total_miss_dollars_abs: number;
  auto_win_dollars: number;
  real_concession_dollars: number;
  buckets: Record<string, RuPerformanceHonestyBucket>;
  n_lines: number;
  n_encounters: number;
  n_distinct_cpts: number;
  sensai_additional_allowed: number;
  net_lift_after_real_concession: number;
  net_lift_pct: number;
}

export interface RuPerformance {
  scope: string;
  dos_from: string | null;
  dos_to: string | null;
  kpis: RuPerformanceKpis;
  monthly: RuPerformanceMonth[];
  providers: RuPerformanceProvider[];
  coverage_funnel: RuPerformanceFunnelStep[];
  honesty: RuPerformanceHonesty;
  meta: {
    corpus_run_id: string | null;
    dos_min_iso: string | null;
    dos_max_iso: string | null;
    snapshot_built_at: number;
  };
}

export async function fetchRevenuePerformance(opts: {
  dos_from?: string;
  dos_to?: string;
  start_date?: string;
  end_date?: string;
} = {}): Promise<RuPerformance> {
  const { data } = await apiClient.get("/revenue/performance", {
    params: opts,
  });
  return data;
}


// ── Opportunity report (AMA-justified-but-never-billed) ────────────────────
// Surfaces (CPT, payer_bucket) pairs where Sensai emits primary-tier
// predictions but the practice rarely bills the code -- estimated dollars
// the practice is leaving on the table. Driven by the build_opportunities
// pipeline stage; see backend service app/services/opportunities.py.

export interface RuOpportunityRow {
  cpt: string;
  cpt_description: string;
  payer_bucket: string;
  emits: number;
  truth_billed: number;
  n_opportunity: number;
  avg_allowed: number;
  dollars_left: number;
  family: string;
  sample_eids: string[];
}

export interface RuOpportunityRollup {
  payer_bucket?: string;
  family?: string;
  lines: number;
  dollars: number;
  pairs: number;
}

export interface RuOpportunitySummary {
  n_opportunities: number;
  total_lines: number;
  total_dollars: number;
  by_payer_bucket: RuOpportunityRollup[];
  by_family: RuOpportunityRollup[];
  params?: Record<string, unknown>;
}

export interface RuOpportunityResponse {
  rows: RuOpportunityRow[];
  total: number;
  limit: number;
  offset: number;
  summary: RuOpportunitySummary;
  meta: {
    source_path: string;
    n_opportunities: number;
    total_lines: number;
    total_dollars: number;
  };
}

export async function fetchOpportunities(opts: {
  payer_bucket?: string;
  family?: string;
  cpt_search?: string;
  sort?: "dollars_left" | "n_opportunity" | "emits" | "avg_allowed";
  limit?: number;
  offset?: number;
} = {}): Promise<RuOpportunityResponse> {
  const { data } = await apiClient.get("/revenue/opportunities", {
    params: opts,
  });
  return data;
}

// Drill-down: the sample encounters behind a single (CPT, payer)
// opportunity row, enriched with claim_id / DOS / provider so the user
// can click through to /revenue/review/[id] (mirrors recall-gap drill).
export interface RuOpportunityEncounter {
  encounter_id: string;
  claim_id: string | null;
  dos_iso: string | null;
  provider_display: string | null;
  payer_bucket: string | null;
  is_in_scope: boolean;
  est_allowed: number;
  in_snapshot: boolean;
}

export interface RuOpportunityDrilldown {
  cpt: string;
  payer_bucket: string | null;
  cpt_description: string;
  n_opportunity: number;
  n_sample: number;
  avg_allowed: number;
  dollars_left: number;
  encounters: RuOpportunityEncounter[];
  warning: string;
}

export async function fetchOpportunityDrilldown(
  cpt: string,
  params: { payer_bucket?: string; limit?: number } = {},
): Promise<RuOpportunityDrilldown> {
  const { data } = await apiClient.get(
    `/revenue/opportunities/cpt/${encodeURIComponent(cpt)}`,
    { params },
  );
  return data;
}


// ── Bakeoff: daily Coder+ vs Sensai comparison ─────────────────────────────

/**
 * Default inclusive DOS window (ISO ``YYYY-MM-DD``) the daily bakeoff is bound
 * to. The provider only works this slice, so the browser never needs the full
 * multi-year corpus; bounding the request keeps the backend DOS-bucketing
 * sweep cheap and off the full-corpus cold path. Mirrors the backend
 * ``bakeoff.BAKEOFF_DEFAULT_START`` / ``BAKEOFF_DEFAULT_END``.
 */
export const BAKEOFF_DEFAULT_START = "2026-05-02";
export const BAKEOFF_DEFAULT_END = "2026-05-31";

/** AMA / CMS citation attached to a Sensai-emitted line. Shows up on the
 *  hover tooltip in /bakeoff so the provider can verify the authority for
 *  every additional dollar we ask them to bill. */
export interface RuBakeoffCitation {
  short_reason: string;
  ama_short: string;
  ama_url: string;
}

/** One Sensai-emitted line on a bakeoff encounter card. ``rule_path`` and
 *  ``citation`` together give the biller a 1-click answer to "why is this
 *  CPT defensible?" The shape is a thin extension of RuLine. */
export interface RuBakeoffSensaiLine {
  cpt: string;
  cpt_description?: string | null;
  modifiers?: string[] | null;
  units?: number;
  kind?: string;
  kind_label?: string;
  delta?: string;
  /** Inherited-bill provenance: "inherited" (kept from Coder+), "lift"
   *  (engine add), "manual" (biller add), or "removed" (engine recommends
   *  dropping). Drives how the line is styled in the full Sensai claim. */
  provenance?: "inherited" | "lift" | "manual" | "removed" | string;
  category?: string;
  tier?: string;
  allowed_amount?: number;
  expected_allowed?: number;
  expected_collected?: number;
  /** Display-only reference value for reconciled split-sibling advisory
   *  lines: what the code is worth IF billed here. NOT part of any revenue
   *  total (expected_allowed stays 0 for these). Rendered as
   *  "ref ~$N if billed". */
  reference_allowed?: number;
  source?: string | null;
  evidence_section?: string | null;
  evidence_span?: string | null;
  rule_path?: string | null;
  accept_rate?: number | null;
  accept_bucket?: string | null;
  citation?: RuBakeoffCitation | null;
  /** True when this line is a confident upbill of an E&M Coder+ already
   *  billed at a lower level (e.g. Coder+ 99213 → engine 99214). The line's
   *  dollars are the INCREMENTAL delta (higher-level allowed − billed-level
   *  allowed). `upbill_from_cpt` is the level Coder+ billed. Rendered as
   *  "Upbill to <cpt>" instead of "add". */
  em_upbill?: boolean;
  upbill_from_cpt?: string | null;
  /** True when this is a confident E&M replacement (e.g. Coder+ 99214 → engine
   *  99204 for new patient). The line's dollars are the INCREMENTAL delta (can
   *  be negative for a downcode). `replace_from_cpt` is the code Coder+ billed.
   *  Rendered as "Replace <cpt>" instead of "add". */
  em_replace?: boolean;
  replace_from_cpt?: string | null;
  /** True when this biller-added (manual) line is a level change within an
   *  E&M family Coder+ already billed (e.g. 99213 → 99214), i.e. a recode
   *  rather than a net-new code. `recode_from_cpt` is the level Coder+
   *  billed. Rendered as "biller recode" instead of "biller add". */
  recode?: boolean;
  recode_from_cpt?: string | null;
  /** Biller flagged this added code as already on the CoderPlus claim
   *  (not a net-new add / upbill). Rendered with the "on claim" (inherited,
   *  $0 incremental) treatment. */
  on_claim?: boolean;
  /** >0 when Coder+ posted this exact line more than once and the duplicates
   *  were collapsed onto this survivor. Drives a "duplicate Coder+ removed"
   *  badge so the biller sees the cleanup that happened. */
  duplicate_removed_count?: number;
  /** Advisory billing-warning codes attached to this displayed line
   *  (WARN-ONLY — the line still bills). E.g. "g2211_no_anchor" or
   *  "em_on_preventive_no_problem". Rendered as an amber "verify before
   *  submitting" note; never blocks the line. */
  warnings?: string[];
  /** Client-only stable unique id, assigned when a line is staged in the
   *  bakeoff editor. Used as the React list key so two lines sharing a CPT
   *  (e.g. a repeated multi-billable 96127) each render and edit independently.
   *  Never sent to or read from the server. */
  _lid?: string;
}

/** Coder+ side: one billed line on the original claim. */
export interface RuBakeoffCoderPlusLine {
  cpt: string;
  cpt_description?: string | null;
  modifiers: string[];
  units: number;
  charge_amount: number;
  allowed_amount: number;
  paid_amount: number;
  denial_codes: string[];
  /** >0 when Coder+ posted this exact line more than once; duplicates were
   *  collapsed onto this survivor. */
  duplicate_removed_count?: number;
}

export interface RuBakeoffBillingWarning {
  /** Machine code, e.g. "g2211_no_anchor" | "em_on_preventive_no_problem". */
  code: string;
  /** The offending CPT this warning is about. */
  cpt: string;
  /** Human-readable advisory message shown to the biller. */
  message: string;
}

export interface RuBakeoffEncounter {
  encounter_id: string;
  claim_id: string | null;
  patient_label: string | null;
  patient_id: string | null;
  provider_display: string;
  is_in_scope: boolean;
  payer: string;
  payer_bucket: string;
  dos_iso: string | null;
  visit_type: string | null;
  narrative: string | null;
  is_split_reconciled?: boolean;
  sibling_claim_ids?: string[];
  /**
   * Reconciliation-status fields (the same set ``RuCase`` carries) so the
   * shared ``reconciliationStatus()`` helper derives an identical badge on the
   * bakeoff surface. All optional — when the backend omits them the badge
   * simply hides (graceful degrade), it never crashes.
   */
  has_critical_miss?: boolean;
  has_split_sibling?: boolean;
  has_ghost_unresolved?: boolean;
  is_ghost_sibling?: boolean;
  ghost_resolved?: boolean;
  internal_miss_allowed?: number;
  /** Sibling claim ids the biller tacked on manually (subset of
   *  ``sibling_claim_ids``). Detection missed these; persisted outside the
   *  snapshot so they survive pipeline rebuilds and print on the PDF. */
  manual_sibling_claim_ids?: string[];
  /** True when the encounter has a chart/truth row but no predicted lines
   *  (never run through the extractor). Surfaced as a "NOT YET CODED" badge
   *  instead of a misleading $0. */
  is_uncoded?: boolean;
  merged_claim_ids?: string[];
  merged_encounter_ids?: string[];
  coder_plus_total: number;
  /** Sum of charged amounts on the Coder+ side (filed value, before
   *  payer adjudication). When ``coder_plus_status === "filed_unadjudicated"``
   *  this is the only meaningful Coder+ number — the allowed total is $0
   *  because the payer hasn't paid the claim yet. */
  coder_plus_charges_total?: number;
  /** "adjudicated" — at least one Coder+ line has allowed > 0.
   *  "filed_unadjudicated" — Coder+ filed real CPTs, no remit yet.
   *  "no_lines" — Coder+ submitted no charges (or only Cat-II quality codes). */
  coder_plus_status?: "adjudicated" | "filed_unadjudicated" | "no_lines";
  sensai_additional: number;
  sensai_total: number;
  expected_cash_uplift: number;
  n_sensai_added: number;
  n_coder_plus_billed: number;
  coder_plus_lines: RuBakeoffCoderPlusLine[];
  sensai_lines: RuBakeoffSensaiLine[];
  /** Claim-level advisory billing warnings (WARN-ONLY). Each carries a
   *  machine code (e.g. "g2211_no_anchor"), the offending CPT, and a
   *  human-readable message. The lines themselves are never dropped. */
  billing_warnings?: RuBakeoffBillingWarning[];
  /** Biller free-text notes for this claim (e.g. "Add modifier 25 to 99214"). */
  biller_notes?: string;
  /** If true, claim is excluded from PDF reports (biller-marked obsolete). */
  excluded_from_report?: boolean;
  annotation_last_edited_by?: string;
  annotation_last_edited_at?: string;
}

export interface RuBakeoffTotals {
  n_encounters: number;
  n_with_lift: number;
  coder_plus_total: number;
  /** Sum of ``coder_plus_charges_total`` across all encounters. */
  coder_plus_charges_total?: number;
  /** Number of encounters with ``coder_plus_status === "filed_unadjudicated"``. */
  n_filed_unadjudicated?: number;
  sensai_additional_total: number;
  sensai_total_total: number;
  expected_cash_uplift_total: number;
  lift_pct: number;
}

export interface RuBakeoffDaily {
  selected_dos: string | null;
  available_dates: string[];
  /** Count of undated encounters surfaced under the explicit "Unknown DOS"
   *  bucket. Fetch them via dos="unknown". 0 when none. */
  unknown_dos_count?: number;
  totals: RuBakeoffTotals;
  encounters: RuBakeoffEncounter[];
  filters?: { provider: string | null };
}

export async function fetchBakeoffDaily(opts: {
  dos?: string;
  provider?: string;
  /**
   * Inclusive DOS window (ISO ``YYYY-MM-DD``) the backend bounds the
   * DOS-bucketing sweep to. Defaults to the fixed May 2–31 2026 window so the
   * browser request never triggers a full multi-year corpus scan (which blows
   * the proxy timeout and makes the screen fail to load).
   */
  startDate?: string;
  endDate?: string;
} = {}): Promise<RuBakeoffDaily> {
  const {
    dos,
    provider,
    startDate = BAKEOFF_DEFAULT_START,
    endDate = BAKEOFF_DEFAULT_END,
  } = opts;
  const params: Record<string, string> = {};
  if (dos) params.dos = dos;
  if (provider) params.provider = provider;
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  const { data } = await apiClient.get("/revenue/bakeoff/daily", {
    params,
    timeout: 600_000,
  });
  return data;
}

export async function fetchBakeoffEncounter(
  encounterId: string,
): Promise<RuBakeoffEncounter> {
  const { data } = await apiClient.get(
    `/revenue/bakeoff/encounter/${encodeURIComponent(encounterId)}`,
  );
  return data;
}

// ── 835 / ERA adjudication (electronic remittance advice) ───────────────────
// The MEASURED payer outcome for a submitted claim: paid / denied / adjusted,
// the CARC/RARC reason codes, and the collected $ — parsed from the payer's 835
// remit and joined back to encounters by claim id. Backs HV-5: per-claim
// adjudication in the claim log + measured collected-$ lift in the bakeoff.

/** One CAS adjustment on a service line (group + CARC reason + amount). */
export interface RuEraAdjustment {
  group_code: string; // CO / PR / OA / PI / CR
  group_label: string; // human label ("contractual", "patient_responsibility", …)
  reason_code: string; // CARC (e.g. "45", "197")
  amount: number;
  quantity: number | null;
}

/** One SVC service-line adjudication within a claim. */
export interface RuEraService {
  procedure_code: string;
  modifiers: string[];
  charge_amount: number;
  paid_amount: number;
  allowed_amount: number | null;
  units: number | null;
  revenue_code: string | null;
  adjustments: RuEraAdjustment[];
  carc_codes: string[];
  remark_codes: string[]; // RARC
  patient_responsibility: number;
}

/** The coarse paid / denied / adjusted outcome axis for a claim. */
export type RuEraOutcome = "paid" | "denied" | "adjusted";

/** The per-claim 835/ERA adjudication summary (read-only render payload). */
export interface RuEraAdjudication {
  claim_id: string;
  outcome: RuEraOutcome;
  status_code: string;
  charge_amount: number;
  paid_amount: number;
  /** Explicit alias for paid_amount — the MEASURED collected dollars. */
  collected_amount: number;
  allowed_amount: number;
  patient_responsibility: number;
  payer_name: string | null;
  payer_id: string | null;
  check_or_eft_number: string | null;
  payment_date: string | null;
  carc_codes: string[];
  rarc_codes: string[];
  services: RuEraService[];
  updated_at: string | null;
  first_ingested_at: string | null;
}

/** Aggregate MEASURED collected-$ rollup across all ingested 835s. */
export interface RuEraSummary {
  practice: string;
  n_adjudicated: number;
  collected_total: number;
  charged_total: number;
  allowed_total: number;
  patient_responsibility_total: number;
  counts: { paid: number; denied: number; adjusted: number };
}

/** The 835 adjudication outcome for one encounter (via its claim id), or null. */
export async function fetchEraForEncounter(
  encounterId: string,
): Promise<RuEraAdjudication | null> {
  const { data } = await apiClient.get<{ adjudication: RuEraAdjudication | null }>(
    `/revenue/provider/era/encounter/${encodeURIComponent(encounterId)}`,
  );
  return data.adjudication;
}

/** The 835 adjudication outcome for one claim id, or null when none ingested. */
export async function fetchEraForClaim(
  claimId: string,
): Promise<RuEraAdjudication | null> {
  const { data } = await apiClient.get<{ adjudication: RuEraAdjudication | null }>(
    `/revenue/provider/era/claim/${encodeURIComponent(claimId)}`,
  );
  return data.adjudication;
}

/** Aggregate measured collected-$ rollup across all ingested 835s. */
export async function fetchEraSummary(): Promise<RuEraSummary> {
  const { data } = await apiClient.get<RuEraSummary>("/revenue/provider/era/summary");
  return data;
}

/**
 * Ingest an 835/ERA remit (raw X12 EDI text). Idempotent — re-posting the same
 * remit never duplicates. Returns how many claims were parsed / stored.
 */
export async function ingestEra(
  edi: string,
): Promise<{ practice: string; parsed: number; stored: number; claim_ids: string[] }> {
  const { data } = await apiClient.post("/revenue/provider/era/ingest", { edi });
  return data;
}

/** Live price for a single CPT against an encounter's payer (add-code UX). */
export interface RuBakeoffCptPrice {
  cpt: string;
  expected_allowed: number;
  expected_collected: number;
  already_billed: boolean;
  below_floor: boolean;
  source: string | null;
}

export async function priceBakeoffCpt(
  encounterId: string,
  cpt: string,
): Promise<RuBakeoffCptPrice> {
  const { data } = await apiClient.get(
    `/revenue/bakeoff/encounter/${encodeURIComponent(
      encounterId,
    )}/price/${encodeURIComponent(cpt)}`,
  );
  return data;
}

/** One edited line the biller wants to bill from the bakeoff screen. */
export interface RuBakeoffQueueLine {
  cpt: string;
  modifiers?: string[];
  units?: number;
  tier?: string | null;
  kind?: string | null;
  description?: string | null;
  rule_path?: string | null;
  /** Biller flagged this code as already on the CoderPlus claim (not a
   *  net-new add). Threaded to the add_line event so the bakeoff/PDF render
   *  it as "on claim" rather than "biller add"/"upbill". */
  on_claim?: boolean;
}

/** The persisted live-queue row returned after enqueuing. The /biller
 *  page's live-coded queue renders these; we only need a few fields here
 *  to confirm + link the biller over. */
export interface RuQueuedEntry {
  id: string;
  status: string;
  encounter_id: string | null;
  patient_id: string | null;
  date_of_service: string | null;
  summary?: { n_lines: number; cpts: string[]; n_units: number };
}

/** Park an (edited) bakeoff bill on the live write queue. The biller then
 *  reviews + writes it out to Athena from the /biller queue drawer.
 *  Pass the edited Sensai line set; omit `charges` to enqueue as-is. */
export async function queueBakeoffEncounter(
  encounterId: string,
  charges: RuBakeoffQueueLine[],
  source = "bakeoff",
): Promise<RuQueuedEntry> {
  const { data } = await apiClient.post(
    `/revenue/bakeoff/encounter/${encodeURIComponent(encounterId)}/queue`,
    { charges, source },
  );
  return data;
}


// ── Push billing to Athena (RPA service) from the bakeoff ───────────────────
// Three biller-gated actions: write a single claim now, flag a claim as
// queued-for-Athena, and batch-push the whole day's queued/accepted edits.
// The Test-account toggle (default ON) redirects the appointmentId to the
// practice's fixed sandbox test appointment so the exact payload is verified
// safely; the real practice id is always derived server-side from the biller.

/** Envelope returned by the RPA forward (single / batch). */
export interface RuAthenaPushResult {
  upstream_status: number;
  upstream_url: string;
  response: unknown;
}

export interface RuAthenaWriteSingleResponse {
  encounter_id: string;
  practice: string;
  practice_id: string;
  test_account: boolean;
  appointment_id: string;
  request_body: Record<string, unknown>;
  result: RuAthenaPushResult;
}

/** Action 1 — push ONE claim's billing payload to Athena immediately. */
export async function writeSingleClaimToAthena(
  encounterId: string,
  opts: { testAccount?: boolean; charges?: RuBakeoffQueueLine[] } = {},
): Promise<RuAthenaWriteSingleResponse> {
  const { data } = await apiClient.post(
    `/revenue/bakeoff/athena/encounter/${encodeURIComponent(encounterId)}/write-single`,
    { test_account: opts.testAccount ?? true, charges: opts.charges ?? null },
  );
  return data;
}

/** Action 2 — flag the (edited/accepted) claim as queued-for-Athena. */
export async function queueClaimForAthena(
  encounterId: string,
  opts: { testAccount?: boolean; charges?: RuBakeoffQueueLine[] } = {},
): Promise<{ queued: boolean; id: string; encounter_id: string; test_account: boolean }> {
  const { data } = await apiClient.post(
    `/revenue/bakeoff/athena/encounter/${encodeURIComponent(encounterId)}/queue`,
    { test_account: opts.testAccount ?? true, charges: opts.charges ?? null },
  );
  return data;
}

/**
 * Persist an edited charge set onto the bakeoff human-events overlay WITHOUT
 * writing to the RPA or parking a write-queue row. This is the lightweight
 * auto-save the editor calls on every add / delete / modifier / units change
 * so the edit survives a reload and feeds write-out. Records the SAME
 * `record_edit_events` overlay (keyed by the stable appointment_anchor) the
 * write/queue paths persist, so provider + biller edits converge. Provider-
 * gated server-side: 404 on a cross-provider encounter.
 */
export async function persistClaimEdit(
  encounterId: string,
  opts: { charges: RuBakeoffQueueLine[] },
): Promise<{ persisted: boolean; encounter_id: string; n_lines: number }> {
  const { data } = await apiClient.post(
    `/revenue/bakeoff/athena/encounter/${encodeURIComponent(encounterId)}/persist-edit`,
    { charges: opts.charges },
  );
  return data;
}

export interface RuAthenaWriteBatchResponse {
  practice: string;
  practice_id: string;
  dos: string | null;
  test_account: boolean;
  n_claims_considered: number;
  n_patients_sent: number;
  included: { encounter_id: string; appointment_id: string }[];
  request_body: Record<string, unknown>;
  result: RuAthenaPushResult;
}

/** Action 3 — batch-push every queued/accepted-edit claim on the day. */
export async function writeWholeDayToAthena(
  opts: { dos?: string | null; testAccount?: boolean } = {},
): Promise<RuAthenaWriteBatchResponse> {
  const { data } = await apiClient.post(`/revenue/bakeoff/athena/day/write-batch`, {
    dos: opts.dos ?? null,
    test_account: opts.testAccount ?? true,
  });
  return data;
}


// NOTE (2026-07-10 consolidation): ``runAutoBillNow`` + ``RuAutoBillRunResult``
// (the client for the removed ``POST /auto-bill/run`` sweep) were deleted along
// with the rest of the auto-bill subsystem. There is no manual auto-bill trigger.


/** Tack a sibling claim id onto an encounter (manual link). Persisted
 *  server-side; overlaid onto the bakeoff row's sibling list + printed on the
 *  PDF. Use when Athena split-claim detection missed a real sibling. */
export async function addBakeoffSibling(
  encounterId: string,
  siblingClaimId: string,
  opts: { addedBy?: string; note?: string } = {},
): Promise<{ encounter_id: string; claim_id: string | null; manual_sibling_claim_ids: string[] }> {
  const { data } = await apiClient.post(
    `/revenue/bakeoff/encounter/${encodeURIComponent(encounterId)}/sibling`,
    { sibling_claim_id: siblingClaimId, added_by: opts.addedBy, note: opts.note },
  );
  return data;
}

/** Remove a manually-added sibling link from an encounter. */
export async function removeBakeoffSibling(
  encounterId: string,
  siblingClaimId: string,
): Promise<{ encounter_id: string; manual_sibling_claim_ids: string[] }> {
  const { data } = await apiClient.delete(
    `/revenue/bakeoff/encounter/${encodeURIComponent(
      encounterId,
    )}/sibling/${encodeURIComponent(siblingClaimId)}`,
  );
  return data;
}

// ── Claim annotations (biller notes, exclude from report) ────────────────

export interface RuClaimAnnotation {
  claim_id: string;
  notes: string;
  excluded_from_report: boolean;
  last_edited_by?: string | null;
  last_edited_at?: string | null;
}

export async function getClaimAnnotation(
  claimId: string,
): Promise<RuClaimAnnotation> {
  const { data } = await apiClient.get(
    `/revenue/bakeoff/claims/${encodeURIComponent(claimId)}/annotation`,
  );
  return data;
}

export async function annotateClaim(
  claimId: string,
  notes: string,
  editor?: string,
): Promise<RuClaimAnnotation> {
  const { data } = await apiClient.post(
    `/revenue/bakeoff/claims/${encodeURIComponent(claimId)}/annotate`,
    { notes, editor: editor ?? "biller" },
  );
  return data;
}

export async function toggleExcludeClaim(
  claimId: string,
  excluded: boolean,
  editor?: string,
): Promise<RuClaimAnnotation> {
  const { data } = await apiClient.post(
    `/revenue/bakeoff/claims/${encodeURIComponent(claimId)}/toggle-exclude`,
    { excluded, editor: editor ?? "biller" },
  );
  return data;
}

export interface RuBakeoffReport {
  path: string;
  filename: string;
  selected_dos: string;
  n_encounters: number;
  totals: RuBakeoffTotals;
  /** Ready-to-paste `scp opc-server:~/...pdf .` command. */
  scp_hint: string;
  download_url: string;
}

/** Generate the daily bakeoff PDF on the server (reflects the biller's
 *  accept/reject/suppress decisions for the day). Returns the file path,
 *  a copy-paste scp command, and an HTTP download URL. */
export async function generateBakeoffReport(opts: {
  dos?: string;
  provider?: string;
} = {}): Promise<RuBakeoffReport> {
  const { data } = await apiClient.post("/revenue/bakeoff/report", null, {
    params: opts,
  });
  return data;
}

/** Download a generated bakeoff PDF as a blob (carries auth) and trigger a
 *  browser "Save as" so the biller doesn't need the shell at all. */
export async function downloadBakeoffReport(filename: string): Promise<void> {
  const { data } = await apiClient.get("/revenue/bakeoff/report/download", {
    params: { filename },
    responseType: "blob",
  });
  const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

// ── Bakeoff cleanup sweep ────────────────────────────────────────────────
// Finds parsed-with-note encounters that never got an LLM extraction (the
// silent $0 / "NOT YET CODED" gap), runs them through extractor -> rules ->
// publish, then refreshes the snapshot cache. Backed by a single-worker job
// queue; poll fetchBakeoffCleanupStatus / fetchBakeoffCleanupLog for progress.

export interface RuJob {
  id: string;
  kind: string;
  label: string;
  cmd: string;
  cwd: string;
  status: "queued" | "running" | "succeeded" | "failed" | "cancelled";
  pid: number | null;
  exit_code: number | null;
  created_at: string;
  started_at: string | null;
  ended_at: string | null;
  error: string | null;
  running: boolean;
  done: boolean;
}

export async function startBakeoffCleanup(opts: {
  checkOnly?: boolean;
  parallel?: number;
} = {}): Promise<{ job: RuJob }> {
  const { data } = await apiClient.post("/revenue/bakeoff/cleanup", null, {
    params: {
      check_only: opts.checkOnly ?? false,
      parallel: opts.parallel ?? 8,
    },
  });
  return data;
}

export async function fetchBakeoffCleanupStatus(
  jobId: string,
): Promise<{ job: RuJob }> {
  const { data } = await apiClient.get(
    `/revenue/bakeoff/cleanup/${encodeURIComponent(jobId)}`,
  );
  return data;
}

export async function fetchBakeoffCleanupLog(
  jobId: string,
): Promise<string> {
  const { data } = await apiClient.get(
    `/revenue/bakeoff/cleanup/${encodeURIComponent(jobId)}/log`,
    { responseType: "text" },
  );
  return data;
}

// ── Assessment & Plan (parsed from the encounter note) ───────────────────
//
// Per the focus provider (bf13.docx, 2026-06-01): the Athena Checkout screen
// renders a clean per-problem A&P panel that the biller relies on to
// justify an upcode. We parse the same structured view directly from
// the encounter note (regex over the serving.encounter_html chart blobs) and
// surface it on the bakeoff/review pages so the biller doesn't need to bounce
// back into Athena.

export interface RuAssessmentPlanIcd {
  code: string;
  description: string;
}

export interface RuAssessmentPlanItem {
  item_number: number;
  title: string;
  narrative: string[];
  plan: string[];
  icd_codes: RuAssessmentPlanIcd[];
  orders: string[];
}

export interface RuAssessmentPlan {
  available: boolean;
  found: boolean;
  items: RuAssessmentPlanItem[];
  raw_section_text: string | null;
}

export async function fetchAssessmentPlan(
  encounterId: string,
): Promise<RuAssessmentPlan> {
  const { data } = await apiClient.get(
    `/revenue/cases/${encodeURIComponent(encounterId)}/assessment-plan`,
  );
  return data;
}

// CPT -> ICD-10 best-match (historical co-occurrence restricted to the
// encounter's diagnoses). Drives the per-line ICD-10 chips on the bakeoff
// page and the RPA write payload's dx pointers (same source of truth).
export interface RuDxMatchPick {
  icd10: string;
  count: number;
  matched: boolean;
}

export interface RuDxMatchDiagnosis {
  position: number;
  icd10: string;
  description: string;
}

export interface RuDxMatchCharge {
  cpt: string;
  instance_idx: number;
  dx_pointers_icd10: string[];
  dx_pointers_position: number[];
  matched: boolean;
  edited: boolean;
  /**
   * True for Cat-II $0 performance-measure codes (`\d{4}F`, e.g. 3008F BMI,
   * 3725F depression screen): these report a quality measure and do NOT
   * require a linked ICD-10. The UI shows "no dx required" for these instead
   * of the "none yet — add one" gap prompt. Optional for backward-compat with
   * an older backend that doesn't emit it (treated as false).
   */
  dx_exempt?: boolean;
}

export interface RuDxMatch {
  encounter_id: string;
  top_n: number;
  available_diagnoses: { code: string; description: string }[];
  matches: Record<string, RuDxMatchPick[]>;
  diagnoses: RuDxMatchDiagnosis[];
  charges: RuDxMatchCharge[];
}

export async function fetchDxMatch(
  encounterId: string,
  topN = 4,
): Promise<RuDxMatch> {
  const { data } = await apiClient.get(
    `/revenue/bakeoff/dx-match/encounter/${encodeURIComponent(encounterId)}`,
    { params: { top_n: topN } },
  );
  return data;
}

// Biller correction to one CPT line's ICD-10 diagnosis pointers. The codes
// MUST be drawn from the encounter's available_diagnoses (server rejects an
// off-encounter code). An empty list reverts the line to the auto-match. The
// edit is persisted as a set_dx_pointers human event and overrides the
// auto-match in BOTH the on-screen preview and the RPA write payload.
export interface RuSetDxPointersResult {
  encounter_id: string;
  available_diagnoses: { code: string; description: string }[];
  diagnoses: RuDxMatchDiagnosis[];
  charges: RuDxMatchCharge[];
}

export async function setDxPointers(
  encounterId: string,
  body: { cpt: string; icd10_list: string[]; instance_idx?: number },
): Promise<RuSetDxPointersResult> {
  const { data } = await apiClient.post(
    `/revenue/bakeoff/dx-match/encounter/${encodeURIComponent(encounterId)}/set-pointers`,
    {
      cpt: body.cpt,
      icd10_list: body.icd10_list,
      instance_idx: body.instance_idx ?? 0,
    },
  );
  return data;
}

// ── Code-description reference (read-only) ──────────────────────────────────
// The curated CPT/HCPCS short-description map, served by the isolated
// /revenue/code-descriptions endpoint. Used by the provider billing UI to show
// a human-readable label alongside a code (the code-selection dropdown renders
// "99213 — Office visit, established — low complexity"). Cached module-side so
// the whole worklist shares ONE fetch (the map is static within a session).

export interface RuCodeDescriptions {
  descriptions: Record<string, string>;
  count: number;
  cat_ii_label: string | null;
}

let _codeDescCache: RuCodeDescriptions | null = null;
let _codeDescInflight: Promise<RuCodeDescriptions> | null = null;

/**
 * Fetch the full ``{CODE: description}`` reference map (once per session).
 *
 * The result is memoized; concurrent callers share a single in-flight request.
 * A network failure is NOT cached — the next call retries — so a transient
 * blip doesn't permanently disable descriptions. Best-effort by contract: the
 * UI degrades to bare codes if this never resolves.
 */
export async function getCodeDescriptions(): Promise<RuCodeDescriptions> {
  if (_codeDescCache) return _codeDescCache;
  if (_codeDescInflight) return _codeDescInflight;
  _codeDescInflight = apiClient
    .get<RuCodeDescriptions>("/revenue/code-descriptions")
    .then(({ data }) => {
      _codeDescCache = data;
      return data;
    })
    .finally(() => {
      _codeDescInflight = null;
    });
  return _codeDescInflight;
}

/** Cat-II reporting codes look like ``\d{4}F`` (e.g. 3074F). */
const _CAT_II_RE = /^\d{4}F$/;

/**
 * Resolve a single code's description from an already-loaded map. Cat-II codes
 * that aren't explicitly enumerated fall back to the shared Cat-II label the
 * server sends, matching the backend's ``get_description`` behaviour.
 */
export function describeCode(
  map: RuCodeDescriptions | null,
  code: string,
): string | null {
  if (!map) return null;
  const c = (code || "").trim().toUpperCase();
  if (!c) return null;
  const explicit = map.descriptions[c];
  if (explicit) return explicit;
  if (_CAT_II_RE.test(c)) return map.cat_ii_label;
  return null;
}

// ── Practice Memory (Set A) ─────────────────────────────────────────────────
// Types + fetchers for the /api/revenue/memory/* rollup endpoints (ticket A1).
// The backend owns ALL derivation; these are pure DTOs + a display-only adapter
// that reshapes the memory series into the MonthlyTrendCard's month-keyed
// shape. No classification / bucketing / aggregation happens here.

/** Coverage triple carried on EVERY trend point (never omitted): an improving
 *  line over a tiny/growing subset is misleading, so counted / eligible /
 *  coverage% travel with each point. */
export interface RuMemoryCoverage {
  counted_encounters: number;
  eligible_encounters: number;
  coverage_pct: number;
}

export interface RuMemoryFidelityPoint extends RuMemoryCoverage {
  period: string;
  proposed: number;
  kept: number;
  fidelity_pct: number;
}

/** Observed-window framing — "first observed period -> latest observed period".
 *  NOT "before/after Nicole" (we claim no pre-Nicole baseline). The external
 *  contract (MEMORY_TASKS_EXTERNAL.md B1) uses FLAT keys; the nested
 *  first/latest objects are kept for backward-compat with this internal UI. */
export interface RuMemoryObservedWindow {
  first_period: string | null;
  first_pct: number | null;
  latest_period: string | null;
  latest_pct: number | null;
  delta_pct: number;
  /** @deprecated nested form — prefer the flat first_pct/latest_pct keys. */
  first: { period: string; fidelity_pct: number } | null;
  /** @deprecated nested form — prefer the flat first_pct/latest_pct keys. */
  latest: { period: string; fidelity_pct: number } | null;
}

export interface RuMemoryFidelityTrend {
  practice: string;
  bucket: string;
  generated_at: string;
  series: RuMemoryFidelityPoint[];
  observed_window: RuMemoryObservedWindow;
}

/** RAW human-correction counts per period (v1). No avoidable/exception split. */
export interface RuMemoryEditRatePoint extends RuMemoryCoverage {
  period: string;
  encounters: number;
  edits_total: number;
  human_corrections: number;
  accepted_as_is: number;
  repeat_correction_rate: number;
  new_pattern_rate: number;
  edits_per_encounter: number;
}

export interface RuMemoryEditRateWindow {
  first_period: string | null;
  first_epe: number;
  latest_period: string | null;
  latest_epe: number;
  correction_delta_pct: number;
}

export interface RuMemoryEditRateTrend {
  practice: string;
  bucket: string;
  generated_at: string;
  /** Honest grain: "case" today (line grain waits on stable_line_key rebind). */
  grain: string;
  series: RuMemoryEditRatePoint[];
  observed_window: RuMemoryEditRateWindow;
}

export interface RuMemorySourceLedger {
  practice: string;
  generated_at: string;
  encounters_learned_from: number;
  submitted_claims: number;
  human_billing_decisions: number;
  payer_adjudications: number;
  modifier_corrections: number;
  as_of: string;
}

/** One "Nicole remembers" line signal — every % carries n + a backend-defined
 *  cohort + threshold_met (below-threshold => FE shows "not enough history"). */
export interface RuMemoryLineSignal {
  kind: string;
  label: string;
  value: string;
  n: number;
  cohort: string;
  threshold_met: boolean;
}

export interface RuMemoryLineSignals {
  practice: string;
  generated_at: string;
  cpt: string;
  provider: string | null;
  payer: string | null;
  grain: string;
  min_n: number;
  signals: RuMemoryLineSignal[];
}

export interface RuMemoryIndex {
  practice: string;
  generated_at: string;
  components: {
    proposal_fidelity_pct: number;
    human_correction_rate_epe: number;
    payer_memory_pct: number;
    provider_fingerprint_coverage: number;
    modifier_pattern_coverage: number;
    doc_pattern_coverage: number;
  };
  composite_index: number;
  as_of: string;
}

/** Payer / provider memory drill-down (the "3rd proof card"). Every accept_pct
 *  ships with its denominator (total / n) + (for providers) a cohort string. */
export interface RuMemoryPayerRow {
  payer: string;
  cpt: string;
  accepted: number;
  total: number;
  accept_pct: number;
}
export interface RuMemoryProviderRow {
  provider: string;
  cpt: string;
  accept_pct: number;
  n: number;
  cohort: string;
  threshold_met: boolean;
}
export interface RuMemoryPayerProvider {
  practice: string;
  generated_at: string;
  payers: RuMemoryPayerRow[];
  providers: RuMemoryProviderRow[];
  coverage: {
    lines_scanned: number;
    lines_with_acceptance_history: number;
    source: string;
  };
  min_n: number;
}

export async function fetchMemoryFidelityTrend(opts: {
  bucket?: "week" | "month";
  since?: string;
} = {}): Promise<RuMemoryFidelityTrend> {
  const { data } = await apiClient.get("/revenue/memory/fidelity-trend", { params: opts });
  return data;
}

export async function fetchMemoryEditRateTrend(opts: {
  bucket?: "week" | "month";
  since?: string;
} = {}): Promise<RuMemoryEditRateTrend> {
  const { data } = await apiClient.get("/revenue/memory/edit-rate-trend", { params: opts });
  return data;
}

export async function fetchMemorySourceLedger(): Promise<RuMemorySourceLedger> {
  const { data } = await apiClient.get("/revenue/memory/source-ledger");
  return data;
}

export async function fetchMemoryLineSignals(opts: {
  cpt: string;
  provider?: string;
  payer?: string;
}): Promise<RuMemoryLineSignals> {
  const { data } = await apiClient.get("/revenue/memory/line-signals", { params: opts });
  return data;
}

export async function fetchMemoryIndex(opts: {
  bucket?: "week" | "month";
  since?: string;
} = {}): Promise<RuMemoryIndex> {
  const { data } = await apiClient.get("/revenue/memory/index", { params: opts });
  return data;
}

export async function fetchMemoryPayerProvider(opts: {
  top?: number;
} = {}): Promise<RuMemoryPayerProvider> {
  const { data } = await apiClient.get("/revenue/memory/payer-provider", { params: opts });
  return data;
}

/** Per-provider Coder→Sensai convergence timeline (ticket A6, debt). */
export interface RuMemoryConvergencePoint {
  revision: number;
  convergence_pct: number;
  dos: string | null;
}
export interface RuMemoryConvergenceProvider {
  provider: string;
  points: RuMemoryConvergencePoint[];
}
export interface RuMemoryConvergenceTimeline {
  practice: string;
  /** False => the pipeline jsonl is not yet ingested on this host. */
  source_present: boolean;
  providers: RuMemoryConvergenceProvider[];
}

export async function fetchMemoryConvergenceTimeline(): Promise<RuMemoryConvergenceTimeline> {
  const { data } = await apiClient.get("/revenue/memory/convergence-timeline");
  return data;
}

/**
 * Display-only adapter: reshape a backend memory series into the month-keyed
 * shape MonthlyTrendCard already renders (RuPerformanceMonth[]). This is pure
 * field-renaming for reuse of the existing chart — it performs NO billing
 * classification / aggregation (the backend already bucketed + derived every
 * value). `rightAxisPct` picks which backend field drives the dashed right-axis
 * line (fidelity_pct for the fidelity card; a derived retention% for edits).
 */
export function toMonthlyFidelity(series: RuMemoryFidelityPoint[]): RuPerformanceMonth[] {
  return series.map((p) => ({
    month: p.period,
    n_encounters: p.counted_encounters,
    n_flagged: 0,
    // Reuse the stacked bars to show kept (teal) atop dropped (slate): the
    // backend already computed proposed/kept; we only map fields.
    truth_allowed: Math.max(0, p.proposed - p.kept),
    truth_realized_allowed: Math.max(0, p.proposed - p.kept),
    sensai_additional_allowed: p.kept,
    expected_cash_uplift: p.kept,
    combined_allowed: p.proposed,
    lift_pct: p.fidelity_pct,
    expected_cash_pct: p.coverage_pct,
  }));
}

export function toMonthlyEditRate(series: RuMemoryEditRatePoint[]): RuPerformanceMonth[] {
  return series.map((p) => ({
    month: p.period,
    n_encounters: p.counted_encounters,
    n_flagged: p.human_corrections,
    // Bars: accepted-as-is (teal) atop human-corrections (slate).
    truth_allowed: p.human_corrections,
    truth_realized_allowed: p.human_corrections,
    sensai_additional_allowed: p.accepted_as_is,
    expected_cash_uplift: p.accepted_as_is,
    combined_allowed: p.edits_total,
    // Right-axis line = edits per encounter (lower is better; labeled honestly
    // in the card, never as "avoidable").
    lift_pct: p.edits_per_encounter,
    expected_cash_pct: p.coverage_pct,
  }));
}

